import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import HotelChannelPricingTablesCellsTableCellBase from 'lumen/components/hotel/channel-pricing/tables/cells/table-cell-base';
import HotelChannelPricingTablesCellsTableCellWrapper from 'lumen/components/hotel/channel-pricing/tables/cells/table-cell-wrapper';
import type {
  TdMinorChannelsTableColumnSignature,
  TdMinorChannelsTableRowSignature,
} from 'lumen/components/hotel/channel-pricing/tables/minor-channels-table';
import type { Carate } from 'lumen/models';
import { MEALTYPE_ICONS } from 'lumen/utils/constants/general-constants';

interface HotelChannelPricingTablesCellsMinorChannelsTablePricesPricingRowCellContentSignature {
  Args: {
    column: TdMinorChannelsTableColumnSignature;
    row: TdMinorChannelsTableRowSignature;
  };
}

export default class HotelChannelPricingTablesCellsMinorChannelsTablePricesPricingRowCellContent extends Component<HotelChannelPricingTablesCellsMinorChannelsTablePricesPricingRowCellContentSignature> {
  @service declare dates: Services['dates'];
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];

  get carate(): Carate | undefined {
    const pricingData = this.args.column['pricingDataPerPeriod'];
    const columnId = this.args.column.id;
    const { channelId } = this.args.row;

    return pricingData?.[columnId]?.[channelId as string];
  }

  get formattedDate(): string {
    return this.args.column.day.date.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );
  }

  get hasTooltip(): boolean {
    return Boolean(this.roomName);
  }

  get isInPushingState(): boolean {
    return (
      this.args.column.daysWithActivePricePush.includes(this.args.column.id) ||
      this.pricingAssistantRecommendationActionPoller.datesBeingRefreshed.has(
        this.args.column.id,
      )
    );
  }

  get mealtypeIcon(): IconName | undefined {
    if (this.carate) {
      return MEALTYPE_ICONS[this.carate.mealtypeIncluded];
    }

    return undefined;
  }

  get roomName(): string {
    if (this.carate) {
      return this.carate.roomName;
    }
    return '';
  }

  get showException(): boolean {
    return (
      !this.isInPushingState &&
      this.args.column.daysWithFailedPricePush.includes(this.args.column.id)
    );
  }

  get showMealtypeIcon(): boolean {
    return Boolean(this.mealtypeIcon);
  }

  get value(): string {
    if (this.carate) {
      return this.carate.formattedRate;
    }
    return this.intl.t('general.missing');
  }

  <template>
    <HotelChannelPricingTablesCellsTableCellWrapper
      @hasInteractions={{false}}
      @hasOpenCta={{false}}
      @hasRecommendation={{false}}
      @showException={{this.showException}}
    >
      <HotelChannelPricingTablesCellsTableCellBase
        @icon={{this.mealtypeIcon}}
        @isLoading={{this.isInPushingState}}
        @isPriceCell={{true}}
        @main={{this.value}}
        @showException={{this.showException}}
      />
    </HotelChannelPricingTablesCellsTableCellWrapper>
    {{#if this.hasTooltip}}
      <EmberTooltip
        @side="right"
        @tooltipClass="base-tooltip channel-pricing-table-tooltip"
      >
        <div
          class="channel-pricing-table-tooltip__header flex items-center justify-center p-400"
        >
          <h4>
            {{this.formattedDate}}
          </h4>
        </div>
        <div class="pt-200 pr-400 pb-200 pl-400">
          <p class="text-200--regular">{{this.roomName}}</p>
        </div>
      </EmberTooltip>
    {{/if}}
  </template>
}
